/**
 * SocialMosaic — uniform dark-card social presentation for the creator.
 *
 * Strategy:
 *   - Backend proxies IG cover images through GCS so hotlink-blocked CDN URLs
 *     work. (This covered TikTok thumbnails too until 2026-08-08; the embed
 *     migration below returns no videos, so nothing is proxied for TikTok now.)
 *   - Frontend renders platform content using our own `SocialCard` (dark theme,
 *     uniform sizing) rather than platform-native iframes in the grid — with
 *     one deliberate exception for TikTok, documented below.
 *   - Click a card → `SocialModal` opens with the platform's native iframe
 *     inside, so playback / comments / view-count attribution all happen
 *     on the platform's surface (no creator revenue lost).
 *
 * Layout:
 *   - "All" tab → per-platform horizontal carousels stacked (IG row → TT row → X row).
 *   - Per-platform tab → horizontal carousel of that platform only.
 *   - "Discord" tab → server widget card (unchanged).
 *
 * TikTok exception (2026-08-08):
 *   TikTok publishes no per-video data without per-creator OAuth, so we can't
 *   build our uniform cards for the general population. Their TikTok slot
 *   renders the official Creator Profile embed widget instead — TikTok's own
 *   chrome in a cross-origin iframe, which we can't theme. That's the accepted
 *   trade for a section that actually renders: the scrape it replaced was
 *   rate-limited into a ~95% failure rate.
 *
 *   The card path below is retained and still wins whenever `videos` is
 *   populated — which is how creators who connect their TikTok account will
 *   render, in our dark cards, matching IG/X.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaTwitter, FaTiktok, FaInstagram, FaDiscord, FaCircle, FaShareAlt } from "react-icons/fa";


import { useGetDropXTweets } from "@/hooks/drop/useGetDropXTweets";
import { useGetDropTikTokVideos } from "@/hooks/drop/useGetDropTikTokVideos";
import { useGetDropInstagramPosts } from "@/hooks/drop/useGetDropInstagramPosts";
import { useGetDropDiscordWidget } from "@/hooks/drop/useGetDropDiscordWidget";
import { formatViews, type SocialTab as SocialTabKey } from "./shared";
import { SocialCard, type SocialCardData } from "./SocialCard";
import { SocialModal, type SocialModalPayload } from "./SocialModal";

interface XTweet {
    id: string; url: string; text: string; createdAt: string;
    authorHandle: string; authorDisplayName: string;
    favoriteCount?: number; retweetCount?: number; photoUrl?: string;
    coverImageProxyUrl?: string | null;
}
interface TikTokVid {
    id: string; url: string; authorHandle: string; title?: string;
    embedHtml?: string; thumbnailUrl?: string;
    thumbnailWidth?: number; thumbnailHeight?: number;
    coverImageProxyUrl?: string | null;
}
interface IgPost {
    id: string; shortcode: string; url: string; embedUrl: string;
    displayUrl: string; thumbnailUrl: string;
    width: number; height: number; isVideo: boolean;
    caption: string; likeCount?: number; commentCount?: number;
    coverImageProxyUrl?: string | null;
}
interface DiscordWidget {
    guildId: string;
    guildName: string; presenceCount: number;
    instantInvite?: string; channels?: Array<{ id: string; name: string }>;
}

/*
 * Adapter — map each platform's raw API shape onto our uniform SocialCardData
 * shape so the grid render is identical regardless of source.
 */
function igToCard(p: IgPost): SocialCardData {
    return {
        id: p.shortcode,
        platform: "INSTAGRAM",
        coverImageProxyUrl: p.coverImageProxyUrl,
        fallbackImageUrl: p.displayUrl,
        caption: p.caption,
        likeCount: p.likeCount,
        commentCount: p.commentCount,
        aspectRatio: p.width && p.height ? p.width / p.height : 1,
    };
}

function ttToCard(v: TikTokVid): SocialCardData {
    return {
        id: v.id,
        platform: "TIKTOK",
        coverImageProxyUrl: v.coverImageProxyUrl,
        fallbackImageUrl: v.thumbnailUrl,
        caption: v.title,
        authorDisplayName: v.authorHandle ? `@${v.authorHandle}` : undefined,
        aspectRatio: v.thumbnailWidth && v.thumbnailHeight ? v.thumbnailWidth / v.thumbnailHeight : 9 / 16,
    };
}

function xToCard(t: XTweet): SocialCardData {
    return {
        id: t.id,
        platform: "X",
        coverImageProxyUrl: t.coverImageProxyUrl,
        fallbackImageUrl: t.photoUrl,
        textBody: t.text,
        caption: t.text,
        authorDisplayName: t.authorDisplayName,
        authorHandle: t.authorHandle,
        when: t.createdAt,
        likeCount: t.favoriteCount,
        repostCount: t.retweetCount,
        aspectRatio: t.photoUrl ? 16 / 9 : 1,
    };
}

export const SocialMosaic: React.FC<{ surfaceId?: string; dropSlug?: string }> = ({ surfaceId, dropSlug }) => {
    /* Source-aware fetch — Drop routes through /drops/slug/:slug/content/*, Hub through /hubs/:surfaceId/* */
    const dropXQuery = useGetDropXTweets(dropSlug);
    const dropTiktokQuery = useGetDropTikTokVideos(dropSlug);
    const dropIgQuery = useGetDropInstagramPosts(dropSlug);
    const dropDiscordQuery = useGetDropDiscordWidget(dropSlug);
    const xQuery = dropXQuery;
    const tiktokQuery = dropTiktokQuery;
    const igQuery = dropIgQuery;
    const discordQuery = dropDiscordQuery;

    const xData = xQuery.data as { handle?: string; tweets?: XTweet[] } | null | undefined;
    const tiktokData = tiktokQuery.data as { handle?: string; videos?: TikTokVid[] } | null | undefined;
    const igData = igQuery.data as { handle?: string; posts?: IgPost[] } | null | undefined;
    const discordData = discordQuery.data as { widget?: DiscordWidget } | null | undefined;

    const tweets = xData?.tweets ?? [];
    const tiktoks = tiktokData?.videos ?? [];
    const igPosts = igData?.posts ?? [];
    /*
     * Non-null only when TikTok will actually embed this profile — the backend
     * resolves it against TikTok's profile oEmbed, so a nonexistent, private,
     * or malformed handle arrives as null and the slot stays hidden.
     */
    const tiktokHandle = tiktokData?.handle ?? null;

    const [modalPayload, setModalPayload] = useState<SocialModalPayload | null>(null);

    const handleCardClick = (data: SocialCardData) => {
        setModalPayload({
            source: data.platform,
            postId: data.id,
            authorHandle: data.authorHandle,
        });
    };

    /*
     * Tabs only appear when there's actual content to show — a configured
     * handle alone doesn't justify a tab if the API returned 0 posts. A tab
     * with no rows underneath would otherwise render a blank content area
     * on click, which reads as broken.
     *
     * TikTok is the exception: an embeddable handle IS its content, because
     * the widget fetches the creator's videos itself. The backend only returns
     * a handle once TikTok has confirmed it embeds, so this can't produce the
     * blank-tab case the rule above guards against.
     */
    const hasTikTok = tiktoks.length > 0 || !!tiktokHandle;
    const available = useMemo(() => {
        const list: Array<{ tab: SocialTabKey | "ALL"; label: string; icon: React.ReactNode }> = [];
        const hasAny = tweets.length > 0 || hasTikTok || igPosts.length > 0;
        if (hasAny) list.push({ tab: "ALL", label: "All", icon: <FaCircle /> });
        if (tweets.length > 0) list.push({ tab: "X", label: "X", icon: <FaTwitter /> });
        if (hasTikTok) list.push({ tab: "TIKTOK", label: "TikTok", icon: <FaTiktok /> });
        if (igPosts.length > 0) list.push({ tab: "INSTAGRAM", label: "Instagram", icon: <FaInstagram /> });
        if (discordData?.widget) list.push({ tab: "DISCORD", label: "Discord", icon: <FaDiscord /> });
        return list;
    }, [tweets.length, hasTikTok, igPosts.length, discordData?.widget]);

    const [activeSocial, setActiveSocial] = useState<SocialTabKey | "ALL" | null>(null);

    useEffect(() => {
        if (activeSocial && !available.find(a => a.tab === activeSocial)) {
            setActiveSocial(null);
        }
    }, [surfaceId, available, activeSocial]);

    const effective = activeSocial && available.find(a => a.tab === activeSocial)
        ? activeSocial
        : available[0]?.tab ?? null;

    /*
     * Suppress the empty state while ANY social query is still in-flight —
     * otherwise a "no social accounts" message flashes on first paint
     * before the IG/TT/X/Discord queries return.
     */
    const isAnySocialLoading = xQuery.isLoading || tiktokQuery.isLoading || igQuery.isLoading || discordQuery.isLoading;

    if (available.length === 0) {
        if (isAnySocialLoading) {
            return <div className="hcs-social hcs-social--loading" aria-label="Loading social content" />;
        }
        return (
            <div className="hcs-empty">
                <FaShareAlt />
                <h3>No social posts yet</h3>
                <p>When the creator posts on Instagram, TikTok, or X, their latest content surfaces here.</p>
            </div>
        );
    }

    if (effective === "DISCORD" && discordData?.widget) {
        return (
            <div className="hcs-social">
                <SocialTabBar available={available} active={effective} onChange={setActiveSocial} />
                <DiscordCard widget={discordData.widget} />
            </div>
        );
    }

    /*
     * On ALL → render per-platform horizontal rows (caps so the page stays scannable).
     * On a single-platform tab → render that platform only, more items per row.
     */
    const isAll = effective === "ALL";
    const perPlatformCap = isAll ? 6 : 12;
    const igCards = igPosts.slice(0, perPlatformCap).map(igToCard);
    const ttCards = tiktoks.slice(0, perPlatformCap).map(ttToCard);
    const xCards = tweets.slice(0, perPlatformCap).map(xToCard);

    const showIG = isAll || effective === "INSTAGRAM";
    const showTT = isAll || effective === "TIKTOK";
    const showX = isAll || effective === "X";

    /*
     * Loading shape — render a skeleton row in TikTok's slot (and only
     * TikTok's slot) while its query is still in-flight. Reason: when the
     * Instagram query resolves before TikTok, the IG row paints alone and
     * then TikTok slides in ABOVE it — a noticeable layout shift on a hero
     * page. Reserving the slot keeps TikTok visually on top from the first
     * paint, matching the locked render order. IG / X don't get skeletons
     * because they slot beneath TikTok; later arrival doesn't shift content
     * above them.
     */
    const ttStillLoading = !tiktokQuery.isFetched;

    return (
        <div className="hcs-social">
            <SocialTabBar available={available} active={effective} onChange={setActiveSocial} />

            {showTT && ttCards.length > 0 &&
                <SocialRow title="TikTok" icon={<FaTiktok />} cards={ttCards} onCardClick={handleCardClick} />
            }
            {showTT && ttCards.length === 0 && tiktokHandle &&
                <TikTokCreatorEmbed handle={tiktokHandle} />
            }
            {showTT && ttCards.length === 0 && !tiktokHandle && ttStillLoading &&
                <SocialRowSkeleton title="TikTok" icon={<FaTiktok />} />
            }
            {showIG && igCards.length > 0 &&
                <SocialRow title="Instagram" icon={<FaInstagram />} cards={igCards} onCardClick={handleCardClick} />
            }
            {showX && xCards.length > 0 &&
                <SocialRow title="X" icon={<FaTwitter />} cards={xCards} onCardClick={handleCardClick} />
            }

            <SocialModal payload={modalPayload} onClose={() => setModalPayload(null)} />
        </div>
    );
};

const TIKTOK_EMBED_SCRIPT_SRC = "https://www.tiktok.com/embed.js";

/** Mirrors the backend's handle charset — guards the innerHTML interpolation below. */
const TIKTOK_HANDLE_PATTERN = /^[A-Za-z0-9_.-]{1,30}$/;

/** How long to let embed.js swap in its iframe before showing the fallback link. */
const TIKTOK_EMBED_FALLBACK_DELAY_MS = 4000;

/**
 * TikTokCreatorEmbed — official Creator Profile widget.
 *
 * TikTok's `embed.js` finds `.tiktok-embed` blockquotes and REPLACES them with
 * a cross-origin iframe. Two consequences drive this implementation:
 *
 *   1. The blockquote is written via `innerHTML`, not JSX. If React owned that
 *      node, it would later try to update or unmount a node TikTok had already
 *      swapped out — the classic `removeChild` crash with third-party embeds.
 *      React owns the container; TikTok owns everything inside it.
 *   2. `embed.js` only scans on load, so on SPA navigation an already-loaded
 *      script never sees the new blockquote. Re-appending the script forces a
 *      fresh scan.
 *
 * The anchor inside the blockquote is the fallback: if the script is blocked
 * or fails, the section degrades to a link to the creator's profile rather
 * than empty space. Its label says where it goes — a bare "@handle" reads as a
 * name, not a destination.
 *
 * Between mount and TikTok's swap the slot shows only that fallback link, which
 * is far shorter than the iframe that replaces it. `.hcs-tiktok-embed` reserves
 * a min-height for exactly that window, so the rows below don't jump — the same
 * layout-shift concern the skeleton above exists to solve for the card path.
 */
const TikTokCreatorEmbed: React.FC<{ handle: string }> = ({ handle }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || !TIKTOK_HANDLE_PATTERN.test(handle)) return;

        const profileUrl = `https://www.tiktok.com/@${handle}`;
        container.innerHTML =
            `<blockquote class="tiktok-embed" cite="${profileUrl}" data-unique-id="${handle}" data-embed-type="creator">` +
            `<section><a target="_blank" rel="noopener noreferrer" href="${profileUrl}?refer=creator_embed">View @${handle} on TikTok</a></section>` +
            "</blockquote>";

        document.querySelector(`script[src="${TIKTOK_EMBED_SCRIPT_SRC}"]`)?.remove();
        const script = document.createElement("script");
        script.src = TIKTOK_EMBED_SCRIPT_SRC;
        script.async = true;
        document.body.appendChild(script);

        /*
         * The fallback link is a degradation path, not a loading state. On a
         * normal load embed.js replaces the whole blockquote and the link goes
         * with it — but until that happens it sits under TikTok's placeholder
         * as visible furniture. Keep it hidden and only reveal it once the swap
         * has clearly not happened (blocked script, consent gate, network),
         * where a link to the creator's profile beats an empty slot.
         */
        const fallbackTimer = window.setTimeout(() => {
            if (!container.querySelector("iframe")) {
                container.classList.add("is-fallback");
            }
        }, TIKTOK_EMBED_FALLBACK_DELAY_MS);

        return () => {
            window.clearTimeout(fallbackTimer);
            script.remove();
            container.classList.remove("is-fallback");
            container.innerHTML = "";
        };
    }, [handle]);

    return (
        <div className="hcs-social-row hcs-social-row--embed">
            <div className="hcs-social-row__header">
                <span className="hcs-social-row__icon"><FaTiktok /></span>
                <h4 className="hcs-social-row__title">TikTok</h4>
            </div>
            <div className="hcs-tiktok-embed" ref={containerRef} />
        </div>
    );
};

const SocialRowSkeleton: React.FC<{ title: string; icon: React.ReactNode }> = ({ title, icon }) =>
    <div className="hcs-social-row hcs-social-row--skeleton" aria-busy="true" aria-label={`${title} loading`}>
        <div className="hcs-social-row__header">
            <span className="hcs-social-row__icon">{icon}</span>
            <h4 className="hcs-social-row__title">{title}</h4>
        </div>
        <div className="hcs-social-row__scroller">
            {[0, 1, 2, 3].map(i => <div key={i} className="hcs-social-card hcs-social-card--skeleton" />)}
        </div>
    </div>;

interface SocialRowProps {
    title: string;
    icon: React.ReactNode;
    cards: SocialCardData[];
    onCardClick: (data: SocialCardData) => void;
}
const SocialRow: React.FC<SocialRowProps> = ({ title, icon, cards, onCardClick }) =>
    <div className="hcs-social-row">
        <div className="hcs-social-row__header">
            <span className="hcs-social-row__icon">{icon}</span>
            <h4 className="hcs-social-row__title">{title}</h4>
        </div>
        <div className="hcs-social-row__scroller">
            {cards.map(card =>
                <SocialCard key={`${card.platform}-${card.id}`} data={card} onClick={onCardClick} />)}
        </div>
    </div>;
interface SocialTabBarProps {
    available: Array<{ tab: SocialTabKey | "ALL"; label: string; icon: React.ReactNode }>;
    active: SocialTabKey | "ALL" | null;
    onChange: (t: SocialTabKey | "ALL") => void;
}
const SocialTabBar: React.FC<SocialTabBarProps> = ({ available, active, onChange }) =>
    <div className="hcs-social__tabs">
        {available.map(opt =>
            <button key={opt.tab} type="button"
                className={`hcs-social__tab${active === opt.tab ? " is-active" : ""}`}
                onClick={() => onChange(opt.tab)}>
                {opt.icon} {opt.label}
            </button>)}
    </div>;
const DiscordCard: React.FC<{ widget: DiscordWidget }> = ({ widget }) =>
    <div className="hcs-discord">
        <div className="hcs-discord__header">
            <FaDiscord /> <strong>{widget.guildName}</strong>
            <span className="hcs-discord__online">
                <FaCircle /> {formatViews(widget.presenceCount)} online
            </span>
        </div>
        {(widget.channels ?? []).length > 0 &&
            <ul className="hcs-discord__channels">
                {(widget.channels ?? []).map(c =>
                    <li key={c.id}>
                        <a
                            href={`https://discord.com/channels/${widget.guildId}/${c.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        ># {c.name}</a>
                    </li>)}
            </ul>
        }
        {widget.instantInvite &&
            <a className="hcs-discord__join" href={widget.instantInvite} target="_blank" rel="noopener noreferrer">
                Join Discord →
            </a>
        }
    </div>;

